import { ASChoiceBar, ASChoiceBarItemType, ComponentSize, DisplayMode } from '@eleventheye/asui';
import useTheme from 'hooks/useTheme';
import React, { useEffect, useState } from 'react';
import {
  GameNavCategory,
  GameNavCategoryType,
  GameNavDefensiveCategory,
  GameNavDefensiveCategoryType,
  GameNavEfficiencyCategory,
  GameNavEfficiencyCategoryType,
  GameNavOffenseCategory,
  GameNavOffenseCategoryType,
  GameSubNavCategory,
} from 'types';

import styles from './StatsNavBar.module.scss';

const navItems: ASChoiceBarItemType[] = [
  { itemId: GameNavCategory.Offense, displayText: GameNavCategory.Offense },
  { itemId: GameNavCategory.Defense, displayText: GameNavCategory.Defense },
  { itemId: GameNavCategory.Efficiency, displayText: GameNavCategory.Efficiency },
];

const offenseNavItems: ASChoiceBarItemType[] = Object.keys(GameNavOffenseCategory).map((itemId) => ({
  itemId: itemId,
  displayText: GameNavOffenseCategory[itemId as GameNavOffenseCategoryType],
}));
const defenseNavItems: ASChoiceBarItemType[] = Object.keys(GameNavDefensiveCategory).map((itemId) => ({
  itemId: itemId,
  displayText: GameNavDefensiveCategory[itemId as GameNavDefensiveCategoryType],
}));
const efficiencyNavItems: ASChoiceBarItemType[] = Object.keys(GameNavEfficiencyCategory).map((itemId) => ({
  itemId: itemId,
  displayText: GameNavEfficiencyCategory[itemId as GameNavEfficiencyCategoryType],
}));

type StatsNavBarProps = {
  className?: string;
  onCategorySelected?: (subCategoryType: GameSubNavCategory) => void;
  frontComponent?: React.ReactNode;
  backComponent?: React.ReactNode;
};

const StatsNavBar: React.FC<StatsNavBarProps> = ({
  className = '',
  onCategorySelected = () => undefined,
  frontComponent,
  backComponent,
}) => {
  const baseCls = `${styles.StatsNavBarContainer} ${className}`;
  const [teamStatsKind, setTeamStatsKind] = useState<GameNavCategoryType>(GameNavCategory.Defense);
  const [teamSubKind, setTeamSubKind] = useState<GameSubNavCategory>('ScoringAgainstBreakdown');
  const [subNavItems, setSubNavItems] = useState<ASChoiceBarItemType[]>(defenseNavItems);

  const onSelectedTeamSubKind = (subKind: GameSubNavCategory) => {
    onCategorySelected(subKind);
    setTeamSubKind(subKind);
  };
  const onSelectedTeamStatsKind = (statsKind: GameNavCategoryType) => {
    setTeamStatsKind(statsKind);

    setSubNavItems(
      statsKind === GameNavCategory.Defense
        ? defenseNavItems
        : statsKind === GameNavCategory.Offense
          ? offenseNavItems
          : efficiencyNavItems
    );

    const subKindCat =
      statsKind === GameNavCategory.Defense
        ? 'ScoringAgainstBreakdown'
        : statsKind === GameNavCategory.Offense
          ? 'ScoringBreakdown'
          : statsKind === GameNavCategory.Efficiency
            ? 'FirstDownEfficiency'
            : 'TimeOfPossession';

    onSelectedTeamSubKind(subKindCat);
  };

  useEffect(() => {
    onSelectedTeamStatsKind(GameNavCategory.Defense);
  }, []);

  const { appTheme } = useTheme();

  const onItemSelectedHandler = (itemId: string) => onSelectedTeamStatsKind(itemId as GameNavCategory);
  const onSubItemSelectedHandler = (itemId: string) => onSelectedTeamSubKind(itemId as GameSubNavCategory);

  return (
    <div className={baseCls}>
      <div className={styles.StatsNavBarFrontAndBackComponents}>
        {!!frontComponent && <div>{frontComponent}</div>}
        <div>
          <ASChoiceBar
            styleOptions={{
              $displayModeOverride: DisplayMode.Desktop,
              $mobileBreakpoint: 425,
            }}
            dwStyleOptions={{ $hideNavArrowButtonsInDW: true, $hideHorizontalScrollbarInDW: true }}
            theme={appTheme}
            size={ComponentSize.Medium}
            navItems={navItems}
            onItemSelected={onItemSelectedHandler}
            selectedItemId={teamStatsKind}
          />
        </div>

        {!!backComponent && <div>{backComponent}</div>}
      </div>

      <div className={styles.StatsNavSubItems}>
        <ASChoiceBar
          styleOptions={{
            $displayModeOverride: DisplayMode.Desktop,
            $mobileBreakpoint: 425,
          }}
          dwStyleOptions={{ $hideNavArrowButtonsInDW: true, $hideHorizontalScrollbarInDW: true }}
          theme={appTheme}
          size={ComponentSize.Small}
          navItems={subNavItems}
          onItemSelected={onSubItemSelectedHandler}
          selectedItemId={teamSubKind}
        />
      </div>
    </div>
  );
};

export default StatsNavBar;
